import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { PoweredByRotowire } from '@sorare/core/src/components/rotowire/PoweredByRotowire';
import { isType } from '@sorare/core/src/gql';

import { GameWeekProjection_AnyPlayerInterface } from './__generated__/index.graphql';

type Props = { player: GameWeekProjection_AnyPlayerInterface };

const ScoreText = styled.text`
  font-weight: bold;
  font-size: var(--fs-12);
  font-family: 'Helvetica';
`;

export const GameWeekProjection = ({ player }: Props) => {
  let projectedScore = null;

  if (isType(player, 'BaseballPlayer')) {
    projectedScore =
      player.rotowireProjectedHitterScore ||
      player.rotowireProjectedPitcherScore;
  }

  if (projectedScore === null) return null;
  return (
    <Tooltip
      title={
        <Vertical gap={0.5} center>
          <FormattedMessage
            id="GameWeekProjection.tooltip"
            defaultMessage="Game Week Projection"
          />
          <PoweredByRotowire />
        </Vertical>
      }
    >
      <svg width={32} height={33} viewBox="0 0 32 33" fill="none">
        <path
          d="M12.6994 0.753381C14.4858 -0.251127 16.6866 -0.251127 18.4729 0.753381L27.6994 5.94173C29.4858 6.94623 30.5862 8.80264 30.5862 10.8117V21.1883C30.5862 23.1974 29.4858 25.0538 27.6994 26.0583L18.4729 31.2466C16.6866 32.2511 14.4858 32.2511 12.6994 31.2466L3.47293 26.0583C1.6866 25.0538 0.586182 23.1974 0.586182 21.1883V10.8117C0.586182 8.80264 1.68661 6.94623 3.47293 5.94173L12.6994 0.753381Z"
          fill="#122335"
        />
        <ScoreText x="50%" y="24" textAnchor="middle" fill="white">
          {Math.floor(projectedScore)}
        </ScoreText>
        <path
          d="M18.2279 1.1892L27.4544 6.37754C29.0872 7.29572 30.0862 8.98777 30.0862 10.8117V21.1883C30.0862 23.0122 29.0872 24.7043 27.4544 25.6225L18.2279 30.8108C16.5937 31.7297 14.5787 31.7297 12.9445 30.8108L3.71801 25.6225C2.08521 24.7043 1.08618 23.0122 1.08618 21.1883V10.8117C1.08618 8.98777 2.08521 7.29572 3.71801 6.37754L12.9445 1.1892C14.5787 0.270267 16.5937 0.270267 18.2279 1.1892Z"
          stroke="white"
          strokeOpacity="0.1"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.1961 5L8.7843 10.1337H10.3529L10.8235 8.57803H11.2941L12.0784 10.1337H13.647L12.8627 8.42245C13.3856 8.31875 14.4314 7.86241 14.4314 6.8668C14.4314 5.62227 13.3333 5 12.8627 5H10.1961ZM11.294 6.2445L10.9803 7.48903H12.0783C12.3398 7.48903 12.8626 7.36459 12.8626 6.86676C12.8626 6.36894 12.5489 6.2445 12.3921 6.2445H11.294ZM15.3624 10.1337L14.9019 5H16.2836L16.4371 7.98084L17.8188 5H19.047L19.2006 7.98084L20.5823 5H22.1176L19.6612 10.1337H18.2794L17.9724 7.48404L16.7442 10.1337H15.3624Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 11.8985H21.1765L21.9608 9.17121L22.7451 11.8985L23.0588 11.0963H24V11.4172H23.2157L22.7451 13.984L21.9608 11.4172L21.4902 12.2193H8V11.8985Z"
          fill="#f7ec57"
        />
      </svg>
    </Tooltip>
  );
};

GameWeekProjection.fragments = {
  anyPlayerInterface: gql`
    fragment GameWeekProjection_AnyPlayerInterface on AnyPlayerInterface {
      slug
      ... on BaseballPlayer {
        slug
        rotowireProjectedHitterScore
        rotowireProjectedPitcherScore
      }
    }
  ` as TypedDocumentNode<GameWeekProjection_AnyPlayerInterface>,
};
